import React, { useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Icon, Button } from 'semantic-ui-react';
import cx from 'classnames';
import BEMHelper from 'react-bem-helper';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-moment';
import { Line } from 'react-chartjs-2';
import { VIEWS } from '../Toolbar/utils';
import { getNewDate } from '../Calendar/utils';
import Loading from '../../Loading';
import Filters from '../../Plan/Filters';
import './ProgressChart.scss';

export const bem = BEMHelper({ name: 'ProgressChart', outputIsString: true });

const colors = {
  'health-fitness': '#3cd6ab',
  life: '#ebce41',
  work: '#ef6a78',
  learn: '#bb64ca',
};

const buddyStyles = {
  'health-fitness': { pointStyle: 'triangle' },
  life: { pointStyle: 'triangle', rotation: 180 },
  work: { pointStyle: 'rectRot' },
  learn: { pointStyle: 'rect' },
};

const dateFormat = new Intl.DateTimeFormat([], {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const ProgressChart = ({ categories, events, handleNewDates, isLoading }) => {
  const [currentDate, setCurrentDate] = useState(moment());

  const classificationRegExp = useMemo(() => {
    const categorySlugs = categories.map((category) => category.slug);
    return new RegExp(`\\bf-(${categorySlugs.join('|')}|circle)\\b`);
  }, [categories]);

  const chartProps = useMemo(() => {
    const startDate = currentDate.clone().startOf('month').toISOString();
    const endDate = currentDate.clone().endOf('month').toISOString();
    const now = new Date().toISOString();

    const allDatasets = [];
    const datasetPairs = new Map();
    for (const category of categories) {
      const self = {
        label: category.name,
        backgroundColor: colors[category.slug],
        borderColor: colors[category.slug],
        pointBackgroundColor: colors[category.slug],
        pointRadius: 6,
        clip: false,
        data: [],
      };
      const buddy = {
        label: category.name + ' Buddy',
        backgroundColor: colors[category.slug],
        borderColor: colors[category.slug],
        borderDash: [7, 3],
        pointBackgroundColor: colors[category.slug],
        pointRadius: 6,
        ...buddyStyles[category.slug],
        clip: false,
        data: [],
      };
      allDatasets.push(self);
      allDatasets.push(buddy);
      datasetPairs.set(category.slug, { self, buddy });
    }

    const visibleEvents = events
      .filter((event) => event.start >= startDate && event.start <= endDate)
      .filter((event) => event.checkInDate || event.checkInWindowEnd <= now);
    for (const event of visibleEvents) {
      const match = event.className.match(classificationRegExp);
      if (match) {
        const classification = match[1];
        const isSelf = classification === 'circle';
        const slug = isSelf ? event.habit.categorySlug : classification;
        const pair = datasetPairs.get(slug);
        if (pair) {
          const dataset = isSelf ? pair.self : pair.buddy;
          dataset.data.push({
            x: event.start,
            y: event.checkIn || 0,
            verb: event.habit.name,
            planName: event.planName,
          });
        }
      }
    }

    const datasets = allDatasets.filter((dataset) => dataset.data.length > 0);

    return {
      data: { datasets },
      options: {
        scales: {
          x: {
            title: { display: true, text: 'Date', font: { weight: 'bold' } },
            type: 'time',
            min: startDate,
            max: endDate,
            grid: { display: false },
          },
          y: {
            title: {
              display: true,
              text: 'Check-in %',
              font: { weight: 'bold' },
            },
            min: 0,
            max: 100,
          },
        },
        plugins: {
          legend: {
            labels: { usePointStyle: true },
          },
          tooltip: {
            callbacks: {
              title: (contexts) =>
                contexts[0] &&
                contexts[0].raw &&
                dateFormat.format(new Date(contexts[0].raw.x)),
              beforeLabel: (context) => context.raw && context.raw.planName,
              label: (context) =>
                context.raw && `${context.raw.verb}: ${context.raw.y}%`,
            },
          },
        },
        maintainAspectRatio: false,
      },
    };
  }, [categories, events, currentDate]);

  const changeCurrentDate = useCallback(
    (date) => {
      setCurrentDate(date);
      const dateStrings = {
        start: moment(date).startOf('month').format('YYYY-MM-DD'),
        end: moment(date).endOf('month').format('YYYY-MM-DD'),
      };
      handleNewDates(dateStrings);
    },
    [setCurrentDate, handleNewDates]
  );

  const onNavigate = useCallback(
    (action) => {
      const newDate = getNewDate({ action, currentDate, view: VIEWS.month });
      changeCurrentDate(newDate);
    },
    [currentDate, changeCurrentDate]
  );

  const onClickToday = useCallback(() => {
    changeCurrentDate(moment());
  }, [changeCurrentDate]);

  if (isLoading) {
    return <Loading />;
  }
  return (
    <div>
      <div className={cx('flex', 'justify-between', bem())}>
        <div className={cx('flex items-start items-center', bem('today'))}>
          <Button basic onClick={onClickToday}>
            Today
          </Button>
        </div>
        <div className={bem('title')}>
          <Icon name="chevron left" onClick={() => onNavigate('PREV')} />
          {currentDate.locale('en').format('MMMM YYYY')}
          <Icon name="chevron right" onClick={() => onNavigate('NEXT')} />
        </div>
        <div className={cx('flex items-center justify-end', bem('settings'))}>
          <Filters />
        </div>
      </div>
      <div className={bem('chart-container')}>
        <Line {...chartProps} />
      </div>
    </div>
  );
};

ProgressChart.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape()),
  events: PropTypes.arrayOf(PropTypes.shape()),
  handleNewDates: PropTypes.func,
  isLoading: PropTypes.bool,
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {})(ProgressChart);
